import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Text, Title } from '@siftscience/focus-components/text'
import { Modal } from '@siftscience/focus-components/modal'
import { Action } from '@siftscience/focus-components/action-bar'
import { Checkbox } from '@siftscience/focus-components/checkbox'
import { TextInput } from '@siftscience/focus-components/input'
import { Search } from '@siftscience/focus-components/icons/Search'
import ImagesList from '../components/ImagesList'
import { ImageCardProps } from '../types'
import axios from '../../../utils/http/axios-local'
import { Portal } from '../../../components/Portal'

const SEARCH_DEBOUNCE = 500

const useStyles = makeStyles(() => ({
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: '4px',
    '& .focus-select-button': {
      width: '220px'
    }
  }
}))

interface SelectImageLibraryModalProps {
  merchantId: number
  showModal: boolean
  setShowModal: (open: boolean) => void
  onAddImage: (images: ImageCardProps[]) => void
}

const SelectImageLibraryModal = ({
  merchantId,
  showModal,
  setShowModal,
  onAddImage
}: SelectImageLibraryModalProps) => {
  const classes = useStyles()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [imagesList, setImagesList] = useState<ImageCardProps[]>([])
  const [totalImagesCount, setTotalImagesCount] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const isSelected = (id: string) => selectedIds.has(id)
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds])

  const MODAL_ACTIONS: Action[] = [
    { label: 'Add Image', disabled: !selectedIds.size }
  ]

  const fetchImagesList = async (
    currentPage: number,
    perPage: number,
    name: string
  ) => {
    setLoading(true)
    try {
      const response = await axios.get('/det/tooling/merchant_library', {
        params: {
          'evidence[mid_ids][]': '',
          sort_by: 'created_at',
          desc: true,
          name: name,
          page: currentPage,
          per_page: perPage,
          merchant_id: merchantId
        },
        paramsSerializer: {
          indexes: null
        }
      })
      setImagesList(response.data.evidences)
      setTotalImagesCount(response.data.total_count)
      return response
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      let next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)

      // next code allows only 1 image to be selected
      if (next.size > 0 && next.has(id)) {
        next = new Set()
        next.add(id)
      } else {
        next = new Set()
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (imagesList.length !== selectedArray.length) {
      setSelectedIds(new Set(imagesList.map(i => i.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const onModalAction = (action: Action) => {
    if (action.label === 'Add Image') {
      const selectedFields = imagesList.filter(image =>
        selectedIds.has(image.id)
      )
      onAddImage(selectedFields)
    }
    if (action.label === 'Cancel') {
      setShowModal(false)
    }
  }

  useEffect(() => {
    if (!merchantId || !currentPage || !perPage) {
      return
    }

    const timerId = setTimeout(() => {
      void fetchImagesList(currentPage, perPage, search)
    }, SEARCH_DEBOUNCE)

    return () => {
      clearTimeout(timerId)
    }
  }, [merchantId, currentPage, perPage, search])

  const onPerPageChange = (value: number) => {
    setPerPage(value)
    setCurrentPage(1)
  }

  const handleSearch = event => {
    setSearch(event.target.value)
  }

  return (
    <Portal>
      <div className="det-modal">
        <Modal
          scrollable={true}
          visible={!!showModal}
          onCloseButtonClick={() => setShowModal(false)}
          variant="fixed"
          footerContent={
            <div className={classes.text}>
              <Title size={'small'}>
                {selectedIds.size} of {imagesList.length || 0} images selected
              </Title>
              <div className={classes.checkbox}>
                <Checkbox
                  checked={imagesList.length === selectedArray.length}
                  onChange={toggleSelectAll}
                />
                <Text size={'xsmall'}>Select all</Text>
              </div>
            </div>
          }
          onAction={onModalAction}
          actions={MODAL_ACTIONS}
          title={
            <div className={classes.headerContainer}>
              <Title>Upload Images</Title>
              <div className={classes.wrapper}>
                <Title size="xsmall" color="secondary">
                  Search
                </Title>
                <TextInput
                  icon={<Search />}
                  size={'large'}
                  placeholder={'Search by title'}
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </div>
          }
          size="full"
        >
          <ImagesList
            merchantId={merchantId}
            isLoading={loading}
            images={imagesList}
            isSelected={isSelected}
            onToggleSelect={toggleSelect}
            totalCount={totalImagesCount}
            onPageChange={setCurrentPage}
            onPerPageChange={onPerPageChange}
            currentPage={currentPage}
            perPage={perPage}
          />
        </Modal>
      </div>
    </Portal>
  )
}

export default SelectImageLibraryModal
